
import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';

const TermsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <div className="container mx-auto px-4 py-12 max-w-3xl flex-grow">
        <h1 className="text-3xl font-bold mb-8 text-center">Terms of Service</h1>
        
        <Card className="mb-8">
          <CardContent className="pt-6">
            <p className="mb-4">
              Last updated: April 17, 2025
            </p>
            
            <p className="mb-6">
              Welcome to Krix.app! These Terms and Conditions ("Terms") govern your use of our website and subscription-based services ("Services"). By accessing or using our Services, you agree to comply with and be bound by these Terms. If you do not agree, please refrain from using our Services.
            </p>
            
            <p className="mb-6">
              These Terms constitute a binding agreement between you ("User") and NXT BIZNESS SOLUTIONS ("Company," "we," "us," or "our"), the legal entity operating Krix.app.
            </p>
            
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-2">1. Legal Entity Information</h2>
                <p className="font-medium">Legal Entity Name:</p>
                <p className="mb-2">NXT BIZNESS SOLUTIONS</p>
                
                <p className="font-medium mt-4">Registered Address:</p>
                <p className="mb-2">402, 6-3-542/1, Golden Green Apartments,</p>
                <p className="mb-2">Panjagutta, Hyderabad, Telangana,</p>
                <p className="mb-2">India, PIN: 500082</p>
                
                <p className="font-medium mt-4">Operational Address:</p>
                <p className="mb-2">402, 6-3-542/1, Golden Green Apartments,</p>
                <p className="mb-2">Panjagutta, Hyderabad, Telangana,</p>
                <p className="mb-2">India, PIN: 500082</p>
                
                <p className="font-medium mt-4">Contact Details:</p>
                <p className="mb-2">Telephone: <a href="tel:+919885390929" className="text-primary hover:underline">+91 9885390929</a></p>
                <p className="mb-2">Email: <a href="mailto:svsashank@gmail.com" className="text-primary hover:underline">svsashank@gmail.com</a></p>
                
                <p className="mt-4">
                  By agreeing to these Terms, you acknowledge that you are entering into a legally binding agreement with NXT BIZNESS SOLUTIONS.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">2. Acceptance of Terms</h2>
                <p>
                  By using Krix.app, you confirm that you have read, understood, and agree to these Terms, along with our Privacy Policy. We reserve the right to modify these Terms at any time without prior notice. It is your responsibility to review them periodically for updates.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">3. Eligibility</h2>
                <p className="mb-2">You must be at least 18 years old to use our Services.</p>
                <p>By registering, you represent and warrant that the information you provide is accurate, complete, and up-to-date.</p>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">4. Subscription Services</h2>
                <p className="mb-2">Krix.app operates on a subscription model. By subscribing, you agree to pay the applicable fees for the selected plan.</p>
                <p className="mb-2">Subscriptions automatically renew at the end of each billing cycle unless canceled before the renewal date.</p>
                <p>You can manage or cancel your subscription through your account settings.</p>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">5. Free Trials</h2>
                <p className="mb-2">We may offer free trials for certain subscription plans. Free trials are subject to these Terms.</p>
                <p>If you do not cancel before the trial period ends, your subscription will automatically convert to a paid plan, and the applicable charges will apply.</p>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">6. Payment and Billing</h2>
                <p className="mb-2">All payments must be made through the payment methods provided on our platform.</p>
                <p className="mb-2">You are responsible for ensuring that your payment information is accurate and up-to-date.</p>
                <p>In case of payment failure, we reserve the right to suspend or terminate your access to the Services.</p>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">7. Refund Policy</h2>
                <p className="mb-2">Refunds are governed by our <a href="/refund" className="text-primary hover:underline">Refund Policy</a>. Please review it for detailed information.</p>
                <p>Refunds are only provided under specific circumstances, such as technical issues or service non-delivery.</p>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">8. User Responsibilities</h2>
                <p className="mb-2">By using our Services, you agree to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Use the Services only for lawful purposes and in compliance with these Terms.</li>
                  <li>Not engage in any activity that disrupts or interferes with the functionality of the website or Services.</li>
                  <li>Not attempt to reverse-engineer, copy, or misuse any part of the platform.</li>
                </ul>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">9. Intellectual Property</h2>
                <p className="mb-4">
                  All content, trademarks, and intellectual property on Krix.app are proprietary to us or our licensors. You are granted a limited, non-exclusive, non-transferable license to use the Services for personal or business purposes as per your subscription plan.
                </p>
                <p>
                  You may not reproduce, distribute, or create derivative works from our content without prior written consent.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">10. Third-Party Links</h2>
                <p>
                  Our website may contain links to third-party websites or services. We are not responsible for the content, terms, or privacy practices of these third-party websites.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">11. Limitation of Liability</h2>
                <p className="mb-4">
                  Neither we nor any third parties provide any warranty or guarantee as to the accuracy, timeliness, performance, completeness, or suitability of the information and materials offered on this website or through the Services for any specific purpose.
                </p>
                <p>
                  To the fullest extent permitted by law, NXT BIZNESS SOLUTIONS and its affiliates shall not be liable for any indirect, incidental, or consequential damages arising from your use of the Services.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">12. Termination</h2>
                <p className="mb-2">We reserve the right to suspend or terminate your account if you violate these Terms or engage in any unlawful activity.</p>
                <p>Upon termination, your access to the Services will cease immediately.</p>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">13. Force Majeure</h2>
                <p>
                  We are not liable for any failure or delay in performance due to events beyond our reasonable control, including but not limited to natural disasters, internet outages, or government actions.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">14. Governing Law and Jurisdiction</h2>
                <p className="mb-2">These Terms are governed by the laws of India.</p>
                <p>Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts in Hyderabad, Telangana.</p>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">15. Refunds and Cancellations</h2>
                <p className="mb-2">You are entitled to claim a refund if we are unable to provide the Services as promised. Refund requests must be made within the time period specified in our Refund Policy.</p>
                <p>If you do not raise a refund claim within the stipulated time, you will no longer be eligible for a refund.</p>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">16. Contact Us</h2>
                <p className="mb-2">If you have any questions or concerns about these Terms, please contact us using the details below:</p>
                <p className="mb-2">Telephone: <a href="tel:+919885390929" className="text-primary hover:underline">+91 9885390929</a></p>
                <p className="mb-2">Email: <a href="mailto:svsashank@gmail.com" className="text-primary hover:underline">svsashank@gmail.com</a></p>
                <p className="font-medium mt-2">Address:</p>
                <p className="mb-2">402, 6-3-542/1, Golden Green Apartments,</p>
                <p className="mb-2">Panjagutta, Hyderabad, Telangana,</p>
                <p className="mb-2">India, PIN: 500082</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Footer />
    </div>
  );
};

export default TermsPage;
